<template>

  <Head :title="$t('admin.menu.agents')" />

  <AdminLayout ref="adminLayout">
    <div class="flex flex-col h-[calc(100vh-90px)] justify-between">
      <div class="flex-1 overflow-auto">
        <div class="flex">
          <div class="flex-1 search-filters">
            <el-form :model="searchModel" label-position="top">
              <div class="grid grid-cols-4 gap-2">
                <div class="col-span-1">
                  <el-form-item prop="keyword" :label="$t('admin.aigc.agent.search.quickSearch')">
                    <el-input v-model="searchModel.keyword" :placeholder="$t('admin.aigc.agent.search.placeholder')" />
                  </el-form-item>
                </div>
                <div class="col-span-1">
                  <el-form-item prop="type" :label="$t('admin.common.type')">
                    <el-select v-model="searchModel.type" :placeholder="$t('admin.common.type')"
                      clearable class="w-full">
                      <el-option v-for="item in typeOptions" :key="item.value" :label="item.label" :value="item.value" />
                    </el-select>
                  </el-form-item>
                </div>
                <div class="col-span-1">
                  <el-form-item prop="createdAt" :label="$t('admin.common.createdAt')">
                    <el-date-picker v-model="searchModel.createdAt" type="daterange" class="w-full" />
                  </el-form-item>
                </div>
              </div>
            </el-form>
          </div>
          <div class="w-100 flex flex-col items-end space-y-2">
            <el-button type="primary" @click="eventHandler('search')">
              <el-icon class="mr-1"><Search /></el-icon>
              {{ $t('common.search') }}
            </el-button>
            <el-button @click="eventHandler('reset')">
              <el-icon class="mr-1"><RefreshRight /></el-icon>
              {{ $t('common.reset') }}
            </el-button>
          </div>
        </div>

        <el-divider class="!mt-[5px] !mb-[10px]" />

        <div class="flex justify-end mb-[10px]">
          <el-button type="primary" @click="openDrawer('new')">
            <el-icon class="mr-1"><Plus /></el-icon>
            {{ $t('admin.aigc.agent.add') }}
          </el-button>
        </div>

        <el-table :loading="loading" row-key="id" :data="rows" :border="false" class="w-full border-t">
          <el-table-column :label="$t('admin.aigc.agent.fields.basic')" min-width="265" fixed="left">
            <template #default="{ row }">
              <div class="cursor-pointer" @click="openDrawer('show', row.id)">
                <div class="text-gray-500 text-sm">ID: {{ row.id }}</div>
                <div class="flex">
                  <el-avatar :size="48" class="min-w-[48px]">
                    <img v-if="row.avatarAttachment" alt="avatar" :src="row.avatarAttachment.url" />
                    <span v-else>{{ row.name && row.name.substr(0, 1) }}</span>
                  </el-avatar>
                  <div class="ml-2">
                    <div class="text-base font-normal text-primary">{{ row.name }}</div>
                    <div class="text-gray-500">{{ row.description }}</div>
                  </div>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column :label="$t('admin.aigc.agent.fields.provider')" width="120">
            <template #default="{ row }">
              {{ row.provider }}
            </template>
          </el-table-column>
          <el-table-column :label="$t('admin.aigc.agent.fields.model')" width="120">
            <template #default="{ row }">
              {{ row.model }}
            </template>
          </el-table-column>
          <el-table-column :label="$t('admin.aigc.agent.fields.type')" prop="type" width="120" />
          <el-table-column :label="$t('admin.aigc.agent.fields.createdAt')" prop="createdAt" width="180" />
          <el-table-column :label="$t('common.operation')" width="120" fixed="right">
            <template #default="{ row }">
              <div class="flex justify-between">
                <el-button link type="primary" @click="openDrawer('edit', row.id)">{{ $t('common.edit') }}</el-button>
                <el-popconfirm :title="$t('common.confirmDelete')" type="warning" 
                  @confirm="router.delete(routes.admin_aigc_agent(row.id))">
                  <template #reference>
                    <el-button link type="danger">{{ $t('common.delete') }}</el-button>
                  </template>
                </el-popconfirm>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <div class="mt-2 flex justify-end border-t pt-[20px]">
        <el-pagination
          v-model:currentPage="pagination.current"
          v-model:page-size="pagination.pageSize"
          :page-sizes="[10, 20, 30, 40, 50]"
          layout="sizes, prev, pager, next, jumper, total"
          :total="pagination.total"
          @current-change="(current) => eventHandler('changePage', { page: current })"
          @size-change="(size) => eventHandler('changePageSize', { size: size })"
        />
      </div>
    </div>

    <template #drawer>
      <Drawer :options="options" ref="drawerRef" @close="adminLayout.closeDrawer()" />
    </template>
  </AdminLayout>
</template>

<script setup>
import AdminLayout from "@/layouts/Admin.vue"
import { Head } from "@inertiajs/vue3"
import Drawer from "./components/Drawer.vue"
import { Search, RefreshRight, Plus } from '@element-plus/icons-vue'

const { t } = useI18n()
const { triggerFlash } = useFlash()
const adminLayout = ref(null)
const drawerRef = ref(null)
const typeOptions = [
  { label: 'Wechat Agent', value: 'Aigc::Agents::WechatAgent' }
]

const { loading, pagination, updatePagination, searchModel, eventHandler } = useSearch(routes.admin_aigc_agents(), {
  keyword: "",
  type: "",
  provider: "all",
  createdAt: [],
  page: 1,
  size: 20,
})

const props = defineProps({
  flash: Object,
  pagy: Object,
  rows: Array,
  options: Object
})

updatePagination(props.pagy)

const openDrawer = async (drawerMode, id = 0) => {
  adminLayout.value.openDrawer()
  await nextTick()
  if (drawerRef.value) {
    await drawerRef.value.updateDrawerMode(drawerMode, id)
    adminLayout.value.stopDrawerLoading()
  }
}

watchEffect(() => { if (props.flash) { triggerFlash(props.flash) } })

// 替换 Message 组件为 ElMessage
const Message = {
  error: (message) => ElMessage.error(message),
  success: (message) => ElMessage.success(message)
}
</script>
